import React, { forwardRef } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { State, ActionHandler } from '@components';
import { PaperClipIcon, ArrowUpOnSquareIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { ArrowRightCircleIcon, ArrowDownCircleIcon } from '@heroicons/react/24/solid';
import { deadlineClasses } from '../utils/deadlines';
import { BASE_HEIGHT_CLASS, BASE_HEIGHT_CELL_CLASS } from '../utils/constants';
import { RenderAvatar } from '../utils/userAvatar';
import { RowMiscButton } from '../utils/rowButton';
import { Tooltip } from '@components';
import { useDeal } from './context';

interface TaskItem {
  id: string;
  label: string;
}

interface TaskItemProps {
  classes?: string;
  taskItemId: string;
}

interface TaskContext {
  taskItemById: Record<string, TaskItem>;
  selectedItemIds: string[];
  setSelectedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  availableStates: any[];
  dealObjects: any;
  authStatuses: any;
}

function Clickable({ children, taskItemId, className, href }) {
  return (
    <ActionHandler
      className={className}
      href={href || `/guest/task_items/${taskItemId}`}
      data={{ turbo_frame: 'dialog', turbo_method: 'get', turbo_stream: true }}
    >
      {children}
    </ActionHandler>
  );
}

export const TaskItem = forwardRef<HTMLDivElement, TaskItemProps>(
  ({ classes = '', taskItemId, mode }, ref) => {
    const { taskItemById, availableStates, dealObjects, authStatuses, currentUser } =
      useDeal() as TaskContext;

    const taskItem = get(taskItemById, taskItemId) as TaskItem;
    const label = get(taskItem, 'label');
    const objects = get(dealObjects, get(taskItem, 'target_type'), []);
    const trackable = objects.find(object => Number(object.id) === Number(taskItem.target_id));
    const trackableLabel = get(trackable, 'label');
    const trackableSubtitle = get(trackable, 'subtitle');

    if (!taskItem) {
      return null;
    }

    // fields
    const filesCount = get(taskItem, 'files_count', 0) || 0;
    const currentState = availableStates.find(s => s.id === taskItem.template_status_id);
    const rawEndAt = get(taskItem, 'end_at');
    const endAt = rawEndAt ? moment(rawEndAt).format('D/M/YY') : null;
    const { bg: deadlineBgClasses, text: deadlineTextClasses } = rawEndAt
      ? deadlineClasses(rawEndAt, get(taskItem, 'state') === 'VALIDATED')
      : {};

    const trackableBadge = (
      <RowMiscButton label={trackableLabel} icon={!trackableLabel && PencilSquareIcon} />
    );
    const trackableButton = !!trackableLabel && trackableLabel !== '' && (
      <Tooltip
        content={[trackableLabel, trackableSubtitle ? `(${trackableSubtitle})` : ''].join(' ')}
      >
        {trackableBadge}
      </Tooltip>
    );

    if (mode === 'viewer' && !(taskItem.viewers || []).find(v => v.id === currentUser.id)) {
      return null;
    }

    if (mode === 'editor' && !(taskItem.editors || []).find(v => v.id === currentUser.id)) {
      return null;
    }

    return (
      <div ref={ref} data-role="item" className="group/item relative group bh-white">
        <div
          className={`bg-white hover:bg-slate-50 ${BASE_HEIGHT_CLASS} ${BASE_HEIGHT_CELL_CLASS} z-[1] grid ${classes} divide-x relative z-[1] select-none`}
        >
          <div className="flex items-center text-slate-700 text-sm font-hmedium space-x-3 [&>[data-target='label']]:w-full [&>>[data-target='label']]:flex-shrink">
            <div
              data-target="label"
              className="flex-shrink truncate w-full [&>a]:w-full [&>a]:block"
            >
              <Clickable className="flex-shrink truncate" taskItemId={taskItem.id}>
                {label}
              </Clickable>
            </div>
            <div className="flex-shrink-0 hidden lg:flex items-center space-x-2">
              {trackableButton}
            </div>
          </div>
          <div
            className={`cell !p-0 [&>a]:w-full [&>a]:flex [&>a]:justify-center hidden lg:inline-flex items-center justify-center relative`}
          >
            <Clickable taskItemId={taskItem.id}>
              {filesCount === 0 ? (
                <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
              ) : (
                <span
                  className={`flex items-center space-x-1 text-sm ${
                    taskItem.last_export_at ? 'text-green-600' : 'text-slate-400'
                  }`}
                >
                  <PaperClipIcon className="w-4 h-4" />
                  <span className="font-hmedium">{filesCount}</span>
                </span>
              )}
            </Clickable>
          </div>
          <div className="cell !p-0 relative overflow-hidden overflow-hidden [&>div]:w-full [&>a>button]:w-full [&>div]:overflow-hidden [&>button]:overflow-hidden">
            <Clickable className="flex-shrink truncate" taskItemId={taskItem.id}>
              <State
                height={`${BASE_HEIGHT_CLASS} w-full`}
                label={get(currentState, 'label')}
                color={get(currentState, 'color')}
              />
            </Clickable>
          </div>
          <div
            className={`!p-0 [&>div]:w-full [&>div]:flex [&>div]:justify-center [&>div]:items-center cell hidden lg:inline-flex justify-center items-center text-xs font-hmedium text-slate-500 ${deadlineBgClasses}`}
          >
            <Clickable className="flex-shrink truncate" taskItemId={taskItem.id}>
              <span className={deadlineTextClasses}>{endAt ? endAt : '__/__/__'}</span>
            </Clickable>
          </div>
          <div className="cell [&>div]:w-full hidden lg:flex items-center justify-center [&>div]:flex [&>div]:items-center [&>div]:justify-center !p-0 [&>a]:w-full [&>a]:flex [&>a]:justify-center">
            <Clickable className="flex-shrink truncate" taskItemId={taskItem.id}>
              <RenderAvatar
                user={taskItem.initiator}
                authStatuses={authStatuses}
                guestMode={true}
              />
            </Clickable>
          </div>
          {mode === 'editor' && (
            <div className="flex items-center">
              {['INITIALIZED', 'REJECTED'].includes(taskItem.state) && (
                <Clickable
                  taskItemId={taskItem.id}
                  className="w-full border border-blue-600 bg-white rounded-full text-xs font-hmedium tracking-tight flex items-center text-blue-600 py-1 justify-center items-center hover:bg-blue-600 hover:text-white space-x-1"
                >
                  <ArrowRightCircleIcon className="w-4 h-4 hidden lg:block" />
                  <span className="hidden lg:block">Compléter la démarche</span>
                  <span className="block lg:hidden">Compléter</span>
                </Clickable>
              )}
              {taskItem.state === 'FULFILLED' && (
                <span className="truncate w-full min-w-0 text-xs text-slate-400 flex justify-center text-center items-center">
                  <span className="hidden lg:block">Informations reçues, merci.</span>
                  <span className="block lg:hidden">Reçu</span>
                </span>
              )}
              {taskItem.state === 'VALIDATED' && (
                <span className="runcate w-full min-w-0 text-xs text-slate-400 flex justify-center text-center items-center">
                  <span className="hidden lg:block">Informations validées, merci.</span>
                  <span className="block lg:hidden">Validé</span>
                </span>
              )}
            </div>
          )}
          {mode === 'viewer' && (
            <div className="flex items-center text-center justify-center">
              {filesCount >= 1 ? (
                <a
                  data-turbo="false"
                  href={`/guest/task_items/${taskItem.id}/download`}
                  className="w-full border border-blue-600 bg-white rounded-full text-xs font-hmedium tracking-tight flex items-center text-blue-600 py-1 justify-center items-center hover:bg-blue-600 hover:text-white space-x-1"
                >
                  <ArrowDownCircleIcon className="w-4 h-4 hidden lg:block" />
                  <span>Télécharger</span>
                </a>
              ) : (
                <div className="flex items-center">
                  <span className="runcate w-full min-w-0 text-xs text-slate-400 flex justify-center text-center items-center">
                    <span className="hidden lg:block">Démarche en lecture seule.</span>
                    <span className="block lg:hidden">Lecture seule</span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);
